import {type ComponentType, useEffect, useState} from 'react';
import {merge} from 'rxjs';

import {
  ChatInput,
  type ChatInputProps,
  useConversationTypewriting,
} from '@stripe-internal/conversations-ui';
import {Conversation} from '@stripe-internal/conversations-client';

import {
  attachmentsEnabled,
  inputEnabled,
  isConversationRestartable,
} from '../lib/util/conversationUtils';
import {useConversationClientPending} from '../hooks/useConversationClientPending';

type Props = {
  conversation: Conversation;
  pending?: boolean;
  focusInput?: boolean;
  ChatInputComponent?: ComponentType<ChatInputProps>;
};

export function AssistantInput({
  conversation,
  pending = false,
  focusInput,
  ChatInputComponent = ChatInput,
}: Props) {
  const [showInput, setShowInput] = useState(inputEnabled(conversation));
  const [disableAttachments, setDisableAttachments] = useState(
    !attachmentsEnabled(conversation),
  );
  const [attachmentsUseStaging, setAttachmentsUseStaging] = useState(
    conversation.context?.channel === 'bot' ||
      (!!conversation.context && isConversationRestartable(conversation)),
  );

  const clientPending = useConversationClientPending(conversation);
  const {typewriting} = useConversationTypewriting();
  const inputPending = clientPending || typewriting || pending;

  useEffect(() => {
    const updateInputConfiguration = () => {
      setShowInput(inputEnabled(conversation));
      setDisableAttachments(!attachmentsEnabled(conversation));
      setAttachmentsUseStaging(
        conversation.context?.channel === 'bot' ||
          (!!conversation.context && isConversationRestartable(conversation)),
      );
    };

    updateInputConfiguration();

    const sub = merge(
      conversation.statusObservable(),
      conversation.contextObservable(),
    ).subscribe(updateInputConfiguration);

    return () => sub.unsubscribe();
  }, [conversation]);

  return (
    <ChatInputComponent
      conversation={conversation}
      attachmentsDisabled={disableAttachments}
      attachmentsUseStaging={attachmentsUseStaging}
      hidden={!showInput}
      pending={inputPending}
      focusInput={focusInput}
    />
  );
}
